// "내 프로필" 탭 — 본인 PIN 로그인 후 보이는 짧은 프로필 카드.
//
// 통계 탭("내 기록")과 분리: 여기는 인적사항/완성도 게이지/편집 진입점만,
// 실제 전적 통계는 통계 탭에서.
//
// 편집 시트 호스트 자체는 legacy.ts(openProfileEditSheet)가 갖고 있으므로
// onEdit 콜백으로 위임 — 저장 후 재렌더링도 호출자가 결정한다.

import { Fragment } from "preact";
import { api } from "../api";
import { adminRoleLabel } from "../auth";
import { navigate } from "../routes";
import { state } from "../state";
import { Icon } from "../ui/components/Icon";
import { BACKHAND_STYLE_LABELS, DOMINANT_HAND_LABELS } from "../ui/format";
import { buildLoginCTA } from "../ui/loginCta";
import { DomNode, mountPreact } from "../ui/preact-mount";
import { buildProfileCompletionBar } from "./index";

export interface RenderMyRecordParams {
  view: HTMLElement;
  onEdit: (member: any, onSaved: () => void) => void;
  rerender: () => void;
}

interface MyRecordViewProps {
  member: any;
  eventCount: number;
  onEdit: () => void;
}

function MyRecordView({ member, eventCount, onEdit }: MyRecordViewProps) {
  const roleLabel = adminRoleLabel();
  const handLabel = member.dominant_hand ? DOMINANT_HAND_LABELS[member.dominant_hand] : null;
  const backhandLabel = member.backhand_style ? BACKHAND_STYLE_LABELS[member.backhand_style] : null;
  const styleSub = [handLabel, backhandLabel].filter(Boolean).join(" · ");
  const completionBar = buildProfileCompletionBar(member);

  return (
    <Fragment>
      <div class="card">
        <div class="card__head-row">
          <p class="card__lead">
            {(member.name || "회원")}님의 프로필
            {roleLabel && <span class="role-badge">{roleLabel}</span>}
          </p>
          <button type="button" class="btn btn--ghost btn--sm" onClick={onEdit}>편집</button>
        </div>
        <p class="card__sub">
          {member.level_category ? member.level_category : "레벨 미설정"} · 누적 {eventCount}개 일정 참여
        </p>
        {styleSub && <p class="card__sub">{styleSub}</p>}
        {completionBar && <DomNode node={completionBar} />}
      </div>
      <button
        type="button"
        class="btn btn--ghost btn--block"
        style="margin-top:12px"
        onClick={() => navigate({ name: "stats" })}
      >
        내 전적 보기 <Icon name="chevron" size={14} />
      </button>
    </Fragment>
  );
}

export async function renderMyRecord(params: RenderMyRecordParams): Promise<void> {
  const { view, onEdit, rerender } = params;
  const memberId = state.session?.actor?.member_id;
  if (!memberId) {
    view.append(buildLoginCTA({
      title: "내 프로필을 보려면 로그인해주세요",
      hint: "참여한 일정 기록과 누적 통계는 본인 PIN으로 로그인한 후에 볼 수 있어요.",
      pendingRoute: { name: "my_record" },
    }));
    return;
  }

  const loading = document.createElement("div");
  loading.className = "loading";
  loading.textContent = "내 프로필 불러오는 중…";
  view.append(loading);

  let data: any;
  try {
    data = await api(`/api/v1/members/${memberId}/event-summaries`);
  } catch (err: any) {
    view.innerHTML = "";
    const errEl = document.createElement("div");
    errEl.className = "error-card";
    errEl.textContent = err.message;
    view.append(errEl);
    return;
  }
  view.innerHTML = "";

  const member = data.member || {};
  const summaries = data.event_summaries || [];
  const eventCount = summaries.length;

  view.append(mountPreact(
    <MyRecordView member={member} eventCount={eventCount} onEdit={() => onEdit(member, rerender)} />,
  ));
}
